/**
 * HubRoutePage — shared route-level component for backend-resolved hubs.
 *
 * B-04: Renders any hub route (Data Hub, Admin Hub, etc.) using the backend
 * resolved descriptor. The page title and cards come from the backend contract,
 * never from hardcoded local UI.
 *
 * HUB-019: Improved API-state handling — distinguishes loading, error,
 * malformed-descriptor, and success states. Validates the hub descriptor
 * has required fields before rendering.
 */
import type { RouteId, NavigationHubDescriptor } from '@finance/contracts';
import { useHub } from '../../runtime/navigation/use-hub';
import { PageLoadingState, PageErrorState, PageEmptyState } from '../states';
import { NavigationHubRenderer } from './NavigationHubRenderer';
import { Box } from '@mui/material';

function isHubDescriptorValid(hub: unknown): hub is NavigationHubDescriptor {
  return (
    typeof hub === 'object' &&
    hub !== null &&
    'titleKey' in hub &&
    typeof (hub as NavigationHubDescriptor).titleKey === 'string' &&
    'groups' in hub
  );
}

export interface HubRoutePageProps {
  hubRouteId: RouteId;
}

export function HubRoutePage({ hubRouteId }: HubRoutePageProps) {
  const { data: hub, isLoading, error, refetch } = useHub(hubRouteId);

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (error) {
    return <PageErrorState reasonCode="HUB_LOAD_FAILED" onRetry={refetch} />;
  }

  if (!hub) {
    return <PageEmptyState />;
  }

  // HUB-019: Validate the descriptor shape before rendering.
  if (!isHubDescriptorValid(hub)) {
    return <PageErrorState reasonCode="HUB_DESCRIPTOR_INVALID" onRetry={refetch} />;
  }

  return (
    <Box data-hub-route-id={hubRouteId} data-hub-state="loaded">
      <NavigationHubRenderer hub={hub} />
    </Box>
  );
}
